import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { useCallback, useState } from 'react';
// @ts-expect-error no types
import * as SimpleDate from 'UIComponents/core/SimpleDate';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import { H2 } from 'UIComponents/elements/headings';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useHasBillingWizardGate } from '../../hooks/useHasGate';
import { useSubscription } from '../../hooks/useSubscription';
import { isNone } from '../../utils/common';
import { EMPTY_VALUE, TEST_IDS, URLS } from '../../utils/constants';
import { ONE_WEEK_IN_MILLISECONDS, createSimpleDate, getDateIntervalDifference } from '../../utils/dateTime';
import { useTracker } from '../../utils/usageTracker';
import { RedAlertBanner } from '../components/RedAlertBanner';
import { StyledSmallObsidian, StyledUITile } from '../components/styledComponents';
import { useEditNextPaymentDate } from './useEditNextPaymentDate';
import { useEditNextPaymentDateQuery } from './useEditNextPaymentDateQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function EditNextPaymentDateModal({
  objectId,
  actions,
  refetchStaleData
}) {
  const hasBillingWizardGate = useHasBillingWizardGate();
  const [isInNoticePeriod, setIsInNoticePeriod] = useState(false);
  const [proposedPaymentDueAt, setProposedPaymentDueAt] = useState(0);
  const isEmptyDate = proposedPaymentDueAt === 0;
  const {
    trackInteraction
  } = useTracker();
  const {
    close
  } = actions;
  const handleClose = useCallback(() => {
    close();
    trackInteraction('updateSubscriptionNextPaymentDateModalClosed');
  }, [close, trackInteraction]);
  const {
    nextPaymentDueDate,
    lastPaymentDate,
    buyer,
    checkoutEmail
  } = useSubscription();
  const doesNotHaveBuyerEmail = isNone(buyer) || !buyer.email;
  const customerEmail = doesNotHaveBuyerEmail ? checkoutEmail : buyer.email;
  const {
    data: queryData,
    loading: queryLoading
  } = useEditNextPaymentDateQuery(objectId);
  const {
    submitUpdateBillingDate,
    submitLoading
  } = useEditNextPaymentDate(objectId, async () => {
    refetchStaleData();
    close();
    await actions.revalidate();
  });
  if (queryLoading || !queryData) {
    return /*#__PURE__*/_jsx(UIModal, {
      "data-test-id": TEST_IDS.EditNextBillingDate,
      children: /*#__PURE__*/_jsxs(UIForm, {
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(H2, {
            className: "p-y-4",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.newTitle' : 'subscriptions.EditNextPaymentDateModal.title'
            })
          }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: handleClose
          })]
        }), /*#__PURE__*/_jsx(UIDialogBody, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(UIAbstractProgress, {
              Component: UINanoProgress,
              maximum: 35
            }), /*#__PURE__*/_jsx(UILoadingSpinner, {
              size: "small",
              minHeight: 100,
              layout: "centered",
              grow: true,
              showLabel: true,
              "data-test-id": "edit-next-payment-date-modal:loading",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.EditNextPaymentDateModal.loadingMessage"
              })
            })]
          })
        })]
      })
    });
  }
  const {
    earliestDueAt,
    subscriptionInvoiceId,
    mutable
  } = queryData;
  const onChange = ({
    target: {
      value
    }
  }) => {
    const proposedDate = SimpleDate.toMomentUTC(value).valueOf();
    const dateIntervalDifference = getDateIntervalDifference(proposedDate, Number(lastPaymentDate));
    if (dateIntervalDifference <= ONE_WEEK_IN_MILLISECONDS) {
      setIsInNoticePeriod(true);
    } else {
      setIsInNoticePeriod(false);
    }
    if (value !== null) {
      setProposedPaymentDueAt(proposedDate);
    } else {
      setProposedPaymentDueAt(0);
    }
  };
  return /*#__PURE__*/_jsx(UIModal, {
    "data-test-id": TEST_IDS.EditNextBillingDate,
    children: /*#__PURE__*/_jsxs(UIForm, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          className: "p-y-4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.newTitle' : 'subscriptions.EditNextPaymentDateModal.title'
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleClose
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [doesNotHaveBuyerEmail && /*#__PURE__*/_jsx(RedAlertBanner, {
          message: "subscriptions.NoAssociatedContactEmailAlert.modalMessage"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.newDescription' : 'subscriptions.EditNextPaymentDateModal.description'
        }), /*#__PURE__*/_jsx(HR, {
          className: "m-y-4"
        }), /*#__PURE__*/_jsx(StyledUITile, {
          className: "p-x-4 p-y-2 m-bottom-3",
          children: /*#__PURE__*/_jsxs(UIGrid, {
            className: "m-y-2",
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(StyledSmallObsidian, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.lastBillingDate' : 'subscriptions.EditNextPaymentDateModal.lastPaymentDate'
                })
              }), /*#__PURE__*/_jsx("p", {
                className: "m-bottom-0",
                children: !lastPaymentDate ? /*#__PURE__*/_jsx("span", {
                  children: EMPTY_VALUE
                }) : /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(lastPaymentDate),
                  format: "ll"
                })
              })]
            }), /*#__PURE__*/_jsxs(UIGridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(StyledSmallObsidian, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.nextBillingDate' : 'subscriptions.EditNextPaymentDateModal.nextPaymentDate'
                })
              }), /*#__PURE__*/_jsx("p", {
                className: "m-bottom-0",
                children: /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(nextPaymentDueDate),
                  format: "ll"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasBillingWizardGate ? 'subscriptions.EditNextPaymentDateModal.datePicker.newTitle' : 'subscriptions.EditNextPaymentDateModal.datePicker.title'
          }),
          children: /*#__PURE__*/_jsx(UIDateInput, {
            "data-test-id": "edit-next-payment-date-picker",
            min: createSimpleDate(earliestDueAt),
            minValidationMessage: I18n.text('subscriptions.EditNextPaymentDateModal.datePicker.minValidationMessage'),
            onChange: onChange
          })
        }), isInNoticePeriod && /*#__PURE__*/_jsx(Small, {
          use: "help",
          className: "p-bottom-0 p-top-2 display-block",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.datePicker.noticePeriodWarningMessage"
          })
        }), /*#__PURE__*/_jsx(HR, {
          className: "m-top-4 m-bottom-4"
        }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "subscriptions.EditNextPaymentDateModal.description_jsx",
          options: {
            href: URLS.KBEmailConfirmation,
            email: customerEmail
          },
          elements: {
            Link: UILink
          }
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UITooltip, {
          disabled: !isEmptyDate,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.emptyDateTooltip"
          }),
          placement: "left",
          children: /*#__PURE__*/_jsx(UILoadingButton, {
            use: "primary",
            onClick: () => submitUpdateBillingDate({
              nextPaymentDueDate,
              subscriptionInvoiceId: subscriptionInvoiceId,
              proposedPaymentDueAt
            }),
            "data-test-id": `${TEST_IDS.EditNextBillingDate}:submit`,
            loading: submitLoading,
            disabled: isEmptyDate || queryLoading || !mutable,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.EditNextPaymentDateModal.update"
            })
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: handleClose,
          "data-test-id": `${TEST_IDS.EditNextBillingDate}:cancel`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.cancel"
          })
        })]
      })]
    })
  });
}